/**
 * Hideable dashboard-hub-table columns (column show/hide) + dashboard list
 * sort/status/date-range helpers.
 *
 * MIRRORS ../reports/reportColumns.ts. This is the single source of truth for the
 * dashboard hub table's hideable columns, sort union, status-badge styles, and the
 * date-range display string. The `id`s MUST match the `isHidden(id)` gates + hide
 * buttons in DashboardTableView. The Name and Actions columns are intentionally
 * PINNED — not listed here, so they can never be hidden. The dashboard table is the
 * report table MINUS the Format column.
 *
 * STATUS / date-range helpers are CLONED from reports (never imported) so the
 * dashboard hub stays decoupled from the frozen report-editor files.
 */
import type { HideableColumn } from "../shared/columnVisibility";
import {
  DATE_RANGE_PRESETS,
  formatDateRangeDisplay,
} from "../../lib/dateRangeUtils";

export const DASHBOARD_HIDEABLE_COLUMNS: HideableColumn[] = [
  { id: "client", label: "Client" },
  { id: "status", label: "Status" },
  { id: "dateRange", label: "Date Range" },
  { id: "updated", label: "Updated" },
  { id: "created", label: "Created" },
];

/**
 * Sort options for the dashboard hub list. Mirrors `ReportSortOption` MINUS the
 * format_asc/format_desc cases (the dashboard table has no Format column).
 */
export type DashboardSortOption =
  | "name_asc"
  | "name_desc"
  | "client_asc"
  | "client_desc"
  | "status_asc"
  | "status_desc"
  | "date_range_asc"
  | "date_range_desc"
  | "created_desc"
  | "created_asc"
  | "updated_desc"
  | "updated_asc";

/**
 * Status badge styles — CLONED from reports' STATUS_STYLES (amber draft / green
 * ready). Cloned, NOT imported, so the dashboard hub never depends on the frozen
 * ReportTableView. Keyed by the dashboard `status` string ("draft" | "ready").
 */
export const DASHBOARD_STATUS_STYLES: Record<
  string,
  { label: string; className: string }
> = {
  draft: {
    label: "Draft",
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  },
  ready: {
    label: "Ready",
    className:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
};

/** The subset of a dashboard row needed to render its date-range cell. */
export interface DashboardDateRangeFields {
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  dateRangePreset: string | null;
}

/**
 * Display string for the dashboard's date range:
 *   - a non-"custom" preset → the preset's label (e.g. "Last 30 days");
 *   - otherwise, resolved start + end → formatDateRangeDisplay(start, end);
 *   - otherwise (no preset, no resolved range) → "—".
 */
export function formatDashboardRange(item: DashboardDateRangeFields): string {
  if (item.dateRangePreset && item.dateRangePreset !== "custom") {
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === item.dateRangePreset);
    if (preset) return preset.label;
  }
  if (item.dateRangeStart && item.dateRangeEnd) {
    return formatDateRangeDisplay(item.dateRangeStart, item.dateRangeEnd);
  }
  return "—";
}
